/**
 * Typisierte Aufrufe der Ticket-Endpunkte. Duenn ueber `lib/api.ts` — die
 * Typen kommen ausschliesslich aus `@tickethub/shared` (Invariante 2).
 *
 * Komponenten rufen diese Funktionen statt `api.get(...)` direkt; die Pfade
 * selbst stehen ausschliesslich in `lib/api-paths.ts` (siehe dort).
 */
import type {
  AiDraft,
  Connection,
  ConnectionDetail,
  ConnectionRef,
  ConnectionTestResult,
  ConnectionType,
  CreateConnectionInput,
  UpdateConnectionInput,
  AiSettings,
  AiSuggestion,
  AllowedFileType,
  Attachment,
  Contact,
  ContactDetail,
  CreateMacroInput,
  CreateKbArticleInput,
  CreateSnippetInput,
  CreateTenantInput,
  CreateTenantMemberInput,
  CreateTenantMemberResult,
  CreateTicketInput,
  CreateTicketMessageInput,
  EmailInboundLogEntry,
  EmailLearnKind,
  EmailLearnResult,
  EmailReprocessResult,
  EmailVerdict,
  FailedJob,
  CsatResponse,
  CsatSummary,
  KbArticle,
  KbArticleDraft,
  Macro,
  MacroRunResult,
  MergeTicketInput,
  NotificationPref,
  Paginated,
  PlatformTenant,
  PresenceEntry,
  Queue,
  SimilarTicket,
  SkilllockSettings,
  SkilllockTaskLink,
  SkilllockWikiSearchResult,
  Snippet,
  SystemMailConfigInput,
  SystemMailConfigPublic,
  SystemMailTestResult,
  Source,
  SourceWithKey,
  SourceWithSecret,
  Tag,
  Team,
  TenantMemberDetail,
  Tenant,
  TicketDetail,
  TicketListItem,
  TicketPriority,
  TicketSort,
  TicketStatus,
  UpdateAiDraftInput,
  UpdateAiSettingsInput,
  UpdateKbArticleInput,
  UpdateMacroInput,
  UpdateNotificationPrefsInput,
  UpdateQueueEmailInput,
  UpdateQueueInput,
  UpdateSkilllockSettingsInput,
  UpdateSourceInput,
  UpdateTenantInput,
  UpdateTicketInput,
  WebhookDelivery,
  WebhookDeliveryStatus,
} from '@tickethub/shared';
import { api } from './api';
import { apiPaths } from './api-paths';

export type TicketFilters = {
  status?: TicketStatus[];
  queue_id?: string;
  assignee_member_id?: string;
  team_id?: string;
  tag_id?: string;
  priority?: TicketPriority;
  contact_id?: string;
  q?: string;
  /** Nur ueberfaellige Tickets (Spec 8.3). */
  overdue?: boolean;
  sort?: TicketSort;
  page?: number;
  per_page?: number;
};

/** Leere Werte fallen raus — sonst stehen `?q=` und `?status=` sinnlos in der URL. */
export function ticketQueryString(filters: TicketFilters): string {
  const params = new URLSearchParams();
  if (filters.status && filters.status.length > 0) params.set('status', filters.status.join(','));
  if (filters.queue_id) params.set('queue_id', filters.queue_id);
  if (filters.assignee_member_id) params.set('assignee_member_id', filters.assignee_member_id);
  if (filters.team_id) params.set('team_id', filters.team_id);
  if (filters.tag_id) params.set('tag_id', filters.tag_id);
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.contact_id) params.set('contact_id', filters.contact_id);
  if (filters.q) params.set('q', filters.q);
  if (filters.overdue) params.set('overdue', 'true');
  if (filters.sort && filters.sort !== 'created_desc') params.set('sort', filters.sort);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.per_page) params.set('per_page', String(filters.per_page));
  const query = params.toString();
  return query.length > 0 ? `?${query}` : '';
}

export const ticketsApi = {
  list: (tenantId: string, filters: TicketFilters) =>
    api.get<Paginated<TicketListItem>>(apiPaths.tickets(tenantId) + ticketQueryString(filters)),

  get: (tenantId: string, ticketId: string) =>
    api.get<TicketDetail>(apiPaths.ticket(tenantId, ticketId)),

  create: (tenantId: string, input: CreateTicketInput) =>
    api.post<TicketDetail>(apiPaths.tickets(tenantId), input),

  update: (tenantId: string, ticketId: string, input: UpdateTicketInput) =>
    api.patch<TicketDetail>(apiPaths.ticket(tenantId, ticketId), input),

  setStatus: (tenantId: string, ticketId: string, status: TicketStatus) =>
    api.patch<TicketDetail>(apiPaths.ticketStatus(tenantId, ticketId), { status }),

  setTags: (tenantId: string, ticketId: string, tagIds: string[]) =>
    api.patch<TicketDetail>(apiPaths.ticketTags(tenantId, ticketId), { tag_ids: tagIds }),

  addMessage: (tenantId: string, ticketId: string, input: CreateTicketMessageInput) =>
    api.post<TicketDetail>(apiPaths.ticketMessages(tenantId, ticketId), input),

  /** Merge (Spec 7.2) — das adressierte Ticket ist die **Quelle**. */
  merge: (tenantId: string, ticketId: string, input: MergeTicketInput) =>
    api.post<{ source: TicketDetail; target: TicketDetail; contacts_merged: boolean }>(
      apiPaths.ticketMerge(tenantId, ticketId),
      input,
    ),

  /** Wer gerade an diesem Ticket sitzt (Spec 7.1) — erster Blick beim Oeffnen. */
  presence: (tenantId: string, ticketId: string) =>
    api.get<{ items: PresenceEntry[] }>(apiPaths.ticketPresence(tenantId, ticketId)),
};

/** Makros (Spec 8.2). Verwalten braucht `tenant.manage`, ausfuehren `ticket.write`. */
export const macrosApi = {
  list: (tenantId: string) => api.get<{ items: Macro[] }>(apiPaths.macros(tenantId)),
  create: (tenantId: string, input: CreateMacroInput) =>
    api.post<Macro>(apiPaths.macros(tenantId), input),
  update: (tenantId: string, macroId: string, input: UpdateMacroInput) =>
    api.patch<Macro>(apiPaths.macro(tenantId, macroId), input),
  remove: (tenantId: string, macroId: string) =>
    api.delete<{ deleted: boolean }>(apiPaths.macro(tenantId, macroId)),
  run: (tenantId: string, ticketId: string, macroId: string) =>
    api.post<MacroRunResult>(apiPaths.ticketMacro(tenantId, ticketId, macroId)),
};

/** Teams (Spec 8.1) — Zuweisungsziel und Benachrichtigungsgruppe. */
export const teamsApi = {
  list: (tenantId: string) => api.get<{ items: Team[] }>(apiPaths.teams(tenantId)),
  members: (tenantId: string, teamId: string) =>
    api.get<{ items: string[] }>(apiPaths.teamMembers(tenantId, teamId)),
  create: (tenantId: string, input: { name: string; member_ids: string[] }) =>
    api.post<Team>(apiPaths.teams(tenantId), input),
  update: (tenantId: string, teamId: string, input: { name?: string; member_ids?: string[] }) =>
    api.patch<Team>(apiPaths.team(tenantId, teamId), input),
  remove: (tenantId: string, teamId: string) =>
    api.delete<{ deleted: boolean }>(apiPaths.team(tenantId, teamId)),
};

/** Benachrichtigungen je Mitglied (Spec 8.5) — jeder aendert nur seine eigenen. */
export const notificationPrefsApi = {
  get: (tenantId: string, memberId: string) =>
    api.get<{ items: NotificationPref[] }>(apiPaths.memberNotificationPrefs(tenantId, memberId)),
  save: (tenantId: string, memberId: string, input: UpdateNotificationPrefsInput) =>
    api.put<{ items: NotificationPref[] }>(
      apiPaths.memberNotificationPrefs(tenantId, memberId),
      input,
    ),
};

/** CSAT (Spec 8.6). */
export const csatApi = {
  summary: (tenantId: string) => api.get<CsatSummary>(apiPaths.csatSummary(tenantId)),
  forTicket: (tenantId: string, ticketId: string) =>
    api.get<{ items: CsatResponse[] }>(apiPaths.ticketCsat(tenantId, ticketId)),
  request: (tenantId: string, ticketId: string) =>
    api.post<{ created: boolean; reason: string | null; csat: CsatResponse | null }>(
      apiPaths.ticketCsat(tenantId, ticketId),
    ),
};

/** SkillLock (Spec 10 / 9.4). */
export const skilllockApi = {
  getSettings: (tenantId: string) =>
    api.get<SkilllockSettings>(apiPaths.skilllockSettings(tenantId)),
  saveSettings: (tenantId: string, input: UpdateSkilllockSettingsInput) =>
    api.put<SkilllockSettings>(apiPaths.skilllockSettings(tenantId), input),

  tasks: (tenantId: string, ticketId: string) =>
    api.get<{ items: SkilllockTaskLink[] }>(apiPaths.ticketSkilllockTasks(tenantId, ticketId)),
  /** 202 — der Aufruf an SkillLock laeuft als Job. */
  createTask: (tenantId: string, ticketId: string, input: { title?: string; board_id?: string | null }) =>
    api.post<{ queued: boolean }>(apiPaths.ticketSkilllockTasks(tenantId, ticketId), input),

  searchWiki: (tenantId: string, query: string) =>
    api.get<SkilllockWikiSearchResult>(
      `${apiPaths.skilllockWikiSearch(tenantId)}?q=${encodeURIComponent(query)}`,
    ),
};

/**
 * Die fuer diesen Mandanten **freigegebenen** Verbindungen (ADR 0017).
 *
 * Die Antwort ist bewusst duenn (`ConnectionRef`): Name, Typ, Provider und bei
 * Mail-Verbindungen die verifizierten Domains. Zugangsdaten sieht hier
 * niemand — die verwaltet der Betreiber unter „Plattform → Verbindungen".
 */
export const connectionsApi = {
  list: (tenantId: string, type?: ConnectionType) =>
    api.get<{ items: ConnectionRef[] }>(
      `${apiPaths.connections(tenantId)}${type ? `?type=${type}` : ''}`,
    ),
};

/** Mandanten-Einstellungen (CSAT an/aus, Slack — Spec 3.2). */
export const tenantApi = {
  get: (tenantId: string) => api.get<Tenant>(apiPaths.tenant(tenantId)),
  update: (tenantId: string, input: UpdateTenantInput) =>
    api.patch<Tenant>(apiPaths.tenant(tenantId), input),
};

export const queuesApi = {
  list: (tenantId: string) => api.get<{ items: Queue[] }>(apiPaths.queues(tenantId)),
  create: (tenantId: string, name: string) =>
    api.post<Queue>(apiPaths.queues(tenantId), { name }),
  update: (tenantId: string, queueId: string, input: UpdateQueueInput) =>
    api.patch<Queue>(apiPaths.queue(tenantId, queueId), input),
  remove: (tenantId: string, queueId: string) =>
    api.delete<{ deleted: boolean }>(apiPaths.queue(tenantId, queueId)),

  /** E-Mail-Konfiguration (Spec 6). Credentials sind write-only. */
  saveEmail: (tenantId: string, queueId: string, input: UpdateQueueEmailInput) =>
    api.put<Queue>(apiPaths.queueEmail(tenantId, queueId), input),
  testEmail: (tenantId: string, queueId: string) =>
    api.post<{ ok: boolean; folder: string; total: number }>(
      apiPaths.queueEmailTest(tenantId, queueId),
    ),
};

/** Eingangsprotokoll je Mandant (Spec 6.3) — Grundlage der Fehlersuche. */
export const emailLogApi = {
  list: (tenantId: string, filters: { verdict?: EmailVerdict; queue_id?: string; page?: number }) => {
    const params = new URLSearchParams();
    if (filters.verdict) params.set('verdict', filters.verdict);
    if (filters.queue_id) params.set('queue_id', filters.queue_id);
    if (filters.page) params.set('page', String(filters.page));
    const query = params.toString();
    return api.get<Paginated<EmailInboundLogEntry>>(
      `${apiPaths.emailLog(tenantId)}${query.length > 0 ? `?${query}` : ''}`,
    );
  },

  /** Quarantaene freigeben (Spec 6.1.1): Rohmail doch noch verarbeiten. */
  process: (tenantId: string, entryId: string) =>
    api.post<EmailReprocessResult>(apiPaths.emailLogProcess(tenantId, entryId)),

  /** Bayes trainieren (Spec 6.1.1) — „Spam" bzw. „Kein Spam". */
  learn: (tenantId: string, entryId: string, kind: EmailLearnKind) =>
    api.post<EmailLearnResult>(apiPaths.emailLogLearn(tenantId, entryId), { kind }),
};

/** Fehlgeschlagene Hintergrund-Jobs (CLAUDE.md, Override „DLQ"). */
export const jobsApi = {
  failed: (tenantId: string) => api.get<{ items: FailedJob[] }>(apiPaths.failedJobs(tenantId)),
  retry: (tenantId: string, queue: string, jobId: string) =>
    api.post<{ retried: boolean }>(apiPaths.failedJobRetry(tenantId, queue, jobId)),
  remove: (tenantId: string, queue: string, jobId: string) =>
    api.delete<{ deleted: boolean }>(apiPaths.failedJob(tenantId, queue, jobId)),
};

export const sourcesApi = {
  list: (tenantId: string) => api.get<{ items: Source[] }>(apiPaths.sources(tenantId)),
  create: (tenantId: string, input: { type: Source['type']; name: string; default_queue_id: string }) =>
    api.post<SourceWithKey>(apiPaths.sources(tenantId), input),
  /** Rueckkanal und Metadaten-Vorgaben (Spec 5.2/5.3). Secret ist write-only. */
  update: (tenantId: string, sourceId: string, input: UpdateSourceInput) =>
    api.patch<Source>(apiPaths.source(tenantId, sourceId), input),
  rotateKey: (tenantId: string, sourceId: string) =>
    api.post<SourceWithKey>(apiPaths.sourceApiKey(tenantId, sourceId)),
  /** Neues Webhook-Secret — der Klartext kommt genau einmal zurueck. */
  rotateWebhookSecret: (tenantId: string, sourceId: string) =>
    api.post<SourceWithSecret>(apiPaths.sourceWebhookSecret(tenantId, sourceId)),
  remove: (tenantId: string, sourceId: string) =>
    api.delete<{ deleted: boolean }>(apiPaths.source(tenantId, sourceId)),
};

/** Zustellprotokoll der Rueckkanal-Webhooks (Spec 5.3). */
export const webhookDeliveriesApi = {
  list: (
    tenantId: string,
    filters: { source_id?: string; status?: WebhookDeliveryStatus; page?: number },
  ) => {
    const params = new URLSearchParams();
    if (filters.source_id) params.set('source_id', filters.source_id);
    if (filters.status) params.set('status', filters.status);
    if (filters.page) params.set('page', String(filters.page));
    const query = params.toString();
    return api.get<Paginated<WebhookDelivery>>(
      `${apiPaths.webhookDeliveries(tenantId)}${query.length > 0 ? `?${query}` : ''}`,
    );
  },
  retry: (tenantId: string, deliveryId: string) =>
    api.post<WebhookDelivery>(apiPaths.webhookDeliveryRetry(tenantId, deliveryId)),
};

export const tagsApi = {
  list: (tenantId: string) => api.get<{ items: Tag[] }>(apiPaths.tags(tenantId)),
  create: (tenantId: string, input: { name: string; color: string }) =>
    api.post<Tag>(apiPaths.tags(tenantId), input),
};

export const snippetsApi = {
  list: (tenantId: string) => api.get<{ items: Snippet[] }>(apiPaths.snippets(tenantId)),
  create: (tenantId: string, input: CreateSnippetInput) =>
    api.post<Snippet>(apiPaths.snippets(tenantId), input),
  update: (tenantId: string, snippetId: string, input: { title?: string; body_json?: unknown }) =>
    api.patch<Snippet>(apiPaths.snippet(tenantId, snippetId), input),
  remove: (tenantId: string, snippetId: string) =>
    api.delete<{ deleted: boolean }>(apiPaths.snippet(tenantId, snippetId)),
};

export const contactsApi = {
  list: (tenantId: string, q?: string) =>
    api.get<{ items: Contact[] }>(
      `${apiPaths.contacts(tenantId)}${q ? `?q=${encodeURIComponent(q)}` : ''}`,
    ),
  get: (tenantId: string, contactId: string) =>
    api.get<ContactDetail>(apiPaths.contact(tenantId, contactId)),
  update: (tenantId: string, contactId: string, input: { notes?: string; display_name?: string }) =>
    api.patch<Contact>(apiPaths.contact(tenantId, contactId), input),
};

export const attachmentsApi = {
  allowedTypes: (tenantId: string) =>
    api.get<{ items: AllowedFileType[] }>(apiPaths.allowedFileTypes(tenantId)),
  upload: (tenantId: string, file: File) =>
    api.upload<Attachment>(apiPaths.attachments(tenantId), file),
};

export const membersApi = {
  list: (tenantId: string) =>
    api.get<{ items: TenantMemberDetail[] }>(apiPaths.members(tenantId)),
  /**
   * Mitglied aufnehmen (Spec 4): bestehender Plattform-User (`user_id`) oder
   * Einladung per `email`. Gibt es den Menschen noch nicht, legt die API ein
   * Konto an und liefert das Startpasswort **einmalig** zurueck.
   */
  add: (tenantId: string, input: CreateTenantMemberInput) =>
    api.post<CreateTenantMemberResult>(apiPaths.members(tenantId), input),
};

/**
 * KI-Funktionen (Spec 9).
 *
 * **Es gibt hier bewusst kein `send`.** Ein Entwurf wird zur Nachricht, indem
 * das UI `ticketsApi.addMessage(..., { ai_draft_id })` aufruft — also den
 * normalen Antwortweg (Architektur-Invariante 3).
 */
export const aiApi = {
  getSettings: (tenantId: string) => api.get<AiSettings>(apiPaths.aiSettings(tenantId)),
  saveSettings: (tenantId: string, input: UpdateAiSettingsInput) =>
    api.put<AiSettings>(apiPaths.aiSettings(tenantId), input),

  listDrafts: (tenantId: string, ticketId: string) =>
    api.get<{ items: AiDraft[] }>(apiPaths.ticketAiDrafts(tenantId, ticketId)),
  /** 202 — der Text kommt aus dem Job, das UI wartet auf `ai.draft_updated`. */
  requestDraft: (tenantId: string, ticketId: string) =>
    api.post<AiDraft>(apiPaths.ticketAiDrafts(tenantId, ticketId)),
  decideDraft: (tenantId: string, draftId: string, input: UpdateAiDraftInput) =>
    api.patch<AiDraft>(apiPaths.aiDraft(tenantId, draftId), input),

  listSuggestions: (tenantId: string, ticketId: string) =>
    api.get<{ items: AiSuggestion[] }>(apiPaths.ticketAiSuggestions(tenantId, ticketId)),
  acceptSuggestion: (tenantId: string, suggestionId: string) =>
    api.post<TicketDetail>(apiPaths.aiSuggestionAccept(tenantId, suggestionId)),
  rejectSuggestion: (tenantId: string, suggestionId: string) =>
    api.post<AiSuggestion>(apiPaths.aiSuggestionReject(tenantId, suggestionId)),

  similar: (tenantId: string, ticketId: string) =>
    api.get<{ items: SimilarTicket[] }>(apiPaths.ticketAiSimilar(tenantId, ticketId)),

  listKb: (tenantId: string) => api.get<{ items: KbArticle[] }>(apiPaths.aiKb(tenantId)),
  createKb: (tenantId: string, input: CreateKbArticleInput) =>
    api.post<KbArticle>(apiPaths.aiKb(tenantId), input),
  updateKb: (tenantId: string, articleId: string, input: UpdateKbArticleInput) =>
    api.patch<KbArticle>(apiPaths.aiKbArticle(tenantId, articleId), input),
  removeKb: (tenantId: string, articleId: string) =>
    api.delete<{ deleted: boolean }>(apiPaths.aiKbArticle(tenantId, articleId)),
  /** „Aus Ticket kuratieren" — liefert einen Entwurf ins Formular. */
  kbDraftFromTicket: (tenantId: string, ticketId: string) =>
    api.post<KbArticleDraft>(apiPaths.ticketAiKbDraft(tenantId, ticketId)),
};

/**
 * Plattformweite Einstellungen (ADR 0014) — ohne `tenantId`, weil sie keinem
 * Mandanten gehoeren. Die API laesst nur Plattform-Admins durch.
 */
export const platformApi = {
  /** Alle Mandanten mit Mitglieder-Zahl — die Betreiber-Uebersicht. */
  tenants: () => api.get<{ items: PlatformTenant[] }>(apiPaths.platformTenants),
  /**
   * Mandant anlegen. Die API macht den Anlegenden in derselben Transaktion
   * zum `admin`-Mitglied; das UI muss danach nur die Session neu laden.
   */
  createTenant: (input: CreateTenantInput) => api.post<Tenant>(apiPaths.tenants, input),

  /** Verbindungs-Vault (ADR 0017) — Geheimnisse sind write-only. */
  connections: () => api.get<{ items: Connection[] }>(apiPaths.platformConnections),
  connection: (connectionId: string) =>
    api.get<ConnectionDetail>(apiPaths.platformConnection(connectionId)),
  createConnection: (input: CreateConnectionInput) =>
    api.post<ConnectionDetail>(apiPaths.platformConnections, input),
  saveConnection: (connectionId: string, input: UpdateConnectionInput) =>
    api.put<ConnectionDetail>(apiPaths.platformConnection(connectionId), input),
  removeConnection: (connectionId: string) =>
    api.delete<{ deleted: boolean }>(apiPaths.platformConnection(connectionId)),
  /** Test-Knopf je Typ; die Testmail geht an die eigene Adresse. */
  testConnection: (connectionId: string) =>
    api.post<ConnectionTestResult>(apiPaths.platformConnectionTest(connectionId)),

  systemMail: () => api.get<SystemMailConfigPublic>(apiPaths.platformSystemMail),
  saveSystemMail: (input: SystemMailConfigInput) =>
    api.put<SystemMailConfigPublic>(apiPaths.platformSystemMail, input),
  /** Testmail an die eigene Adresse — der Empfaenger ist nicht waehlbar. */
  testSystemMail: () =>
    api.post<SystemMailTestResult>(apiPaths.platformSystemMailTest),
};
